import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { categories } from "@/mocks/categories";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/category/otherworld", label: "异世界穿越" },
  { to: "/category/elf", label: "精灵契约" },
  { to: "/category/myth", label: "妖怪神话" },
  { to: "/about", label: "关于我们" },
  { to: "/faq", label: "常见问题" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = keyword.trim()
    ? comics.filter(
        (c) =>
          c.title.includes(keyword.trim()) ||
          c.tags.some((t) => t.includes(keyword.trim())) ||
          c.short.includes(keyword.trim()),
      )
    : [];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-background-200 bg-background-50/90 backdrop-blur-md" : "border-b border-transparent bg-background-50"
      }`}
    >
      <nav className="container-site flex h-16 items-center justify-between md:h-20" aria-label="主导航">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-sparkling-2-fill text-lg" />
          </span>
          <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">
            妖精<span className="text-primary-600">漫画堂</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  active ? "bg-primary-100 text-primary-700" : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <div ref={searchRef} className="relative hidden md:block">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              aria-label="搜索漫画"
            >
              <i className="ri-search-line text-lg" />
            </button>
            {searchOpen && (
              <div className="absolute right-0 top-12 w-80 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-lg">
                <div className="flex items-center gap-2 border-b border-background-100 px-3 py-2.5">
                  <i className="ri-search-line text-foreground-500" />
                  <input
                    autoFocus
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索作品名 / 标签 / 剧情"
                    className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                  />
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {results.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-foreground-500">
                      {keyword.trim() ? "未找到匹配的漫画" : "输入关键词，快速找到你想追的作品"}
                    </p>
                  ) : (
                    results.map((c) => (
                      <Link
                        key={c.id}
                        to={`/comic/${c.id}`}
                        onClick={() => setSearchOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-background-100"
                      >
                        <img src={c.cover} alt={`${c.title} 漫画封面`} className="h-14 w-10 rounded object-cover object-top" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground-900">{c.title}</p>
                          <p className="truncate text-xs text-foreground-500">{c.short}</p>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <Link to="/contact" className="btn btn-primary hidden sm:inline-flex">
            我要投稿
          </Link>

          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-700 lg:hidden"
            aria-label="打开菜单"
          >
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <div className="mb-3 flex items-center gap-2 rounded-md border border-background-200 px-3 py-2">
            <i className="ri-search-line text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
          </div>
          <div className="flex flex-col">
            {navLinks.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`rounded-md px-3 py-2.5 text-sm font-medium ${active ? "bg-primary-100 text-primary-700" : "text-foreground-700"}`}
                >
                  {l.label}
                </Link>
              );
            })}
            <Link to="/contact" className="btn btn-primary mt-2">
              我要投稿
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}